import { useEffect, useState } from "react";
import { api, peso } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { Card } from "@/components/ui/card";
import { Car, ChartBar, Coins, MapPin, Warning, TrendUp } from "@phosphor-icons/react";

function Metric({ label, value, icon: Icon, accent = "text-[#2E7D32]", tid }) {
  return (
    <Card className="bg-white border-[#E7E2D4] p-6 rounded-xl shadow-sm" data-testid={`metric-card-${tid}`}>
      <div className="flex items-start justify-between">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#8A7B5C]">{label}</p>
        <Icon size={18} className="text-[#B8923A]" />
      </div>
      <p className={`mt-3 font-mono text-3xl font-semibold tracking-tight ${accent}`} data-testid={TID.metric(tid)}>
        {value}
      </p>
    </Card>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/executive").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-[#6B705C]">Loading…</div>;

  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#B8923A]">Parking Operations</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1 text-[#1F2933]">
          Community Parking <span className="text-[#4CAF3D]">Dashboard.</span>
        </h1>
        <p className="text-[#5F6B5A] mt-2 max-w-2xl">
          Monitor occupancy, available slots, guest parking, revenue, and incidents across Amaia Steps.
        </p>
      </header>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric tid="occupancy" label="Occupancy" value={`${data.occupancy_rate}%`} icon={ChartBar} accent="text-[#4CAF3D]" />
        <Metric tid="available" label="Available Slots" value={data.available} icon={MapPin} />
        <Metric tid="revenue-today" label="Revenue Today" value={peso(data.revenue_today)} icon={Coins} accent="text-[#B8923A]" />
        <Metric tid="revenue-total" label="Revenue Lifetime" value={peso(data.revenue_total)} icon={TrendUp} accent="text-[#4CAF3D]" />
        <Metric tid="total-slots" label="Total Slots" value={data.total_slots} icon={MapPin} accent="text-[#1F2933]" />
        <Metric tid="occupied" label="Occupied" value={data.occupied} icon={Car} accent="text-[#C96B3A]" />
        <Metric tid="leased" label="Leased" value={data.leased} icon={MapPin} accent="text-[#B8923A]" />
        <Metric tid="violations" label="Open Violations" value={data.open_violations} icon={Warning} accent="text-[#D97706]" />
      </div>

      <Card className="bg-white border-[#E7E2D4] p-8 rounded-xl shadow-sm">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#8A7B5C]">Slot Status Legend</p>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            ["available", "Available"],
            ["occupied", "Occupied"],
            ["owner_reserved", "Owner Reserved"],
            ["leased", "Leased"],
            ["daily_rental", "Daily Rental"],
            ["guest", "Guest"],
            ["staff", "Staff"],
            ["ev", "EV"],
            ["maintenance", "Maintenance"],
          ].map(([k, l]) => (
            <div key={k} className="flex items-center gap-2">
              <div className={`h-5 w-5 rounded slot-${k}`} />
              <span className="text-sm text-[#374151]">{l}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
